import { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Typography, Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { toast } from "sonner";
import { createDoctorSchedule } from "../../services/doctors.service";
import { ILocalInfo } from "../../interfaces/IDoctor";

interface ScheduleModalProps {
  open: boolean;
  onClose: () => void;
  initialDate?: Dayjs | null;
  initialTime?: string | null; // formato HH:mm
  onScheduleCreated?: () => void; // callback para atualizar agenda após salvar
  doctorId: string; // médico para o qual o horário será criado
  localId: string | null; // local de atendimento selecionado para este horário (valor inicial)
  locals?: ILocalInfo[]; // lista de locais existentes para escolha no modal
}

export function ScheduleModal({ open, onClose, initialDate, initialTime, onScheduleCreated, doctorId, localId, locals }: ScheduleModalProps) {
  const [date, setDate] = useState<Dayjs | null>(initialDate ?? dayjs());
  const [startTime, setStartTime] = useState(initialTime ?? "08:00");
  const [endTime, setEndTime] = useState(() => {
    if (initialDate && initialTime) {
      const [h, m] = initialTime.split(":").map(Number);
      const end = initialDate.hour(h).minute(m).add(30, "minute");
      return end.format("HH:mm");
    }
    return "12:00";
  });
  const [loading, setLoading] = useState(false);

  // Local selecionado dentro do modal (padrão: localId vindo de fora ou primeiro da lista)
  const [selectedLocalId, setSelectedLocalId] = useState<string | "">(
    localId ?? (locals && locals.length > 0 ? locals[0].id : "")
  );

  useEffect(() => {
    if (!open) return;
    setDate(initialDate ?? dayjs());
    if (initialTime) {
      setStartTime(initialTime);
      const base = initialDate ?? dayjs();
      const [h, m] = initialTime.split(":").map(Number);
      const end = base.hour(h).minute(m).add(30, "minute");
      setEndTime(end.format("HH:mm"));
    }

    // Sempre que o modal abrir, recalcula o local selecionado com base nas props mais recentes
    const effectiveLocalId =
      localId ?? (locals && locals.length > 0 ? locals[0].id : "");
    setSelectedLocalId(effectiveLocalId || "");
  }, [open, initialDate, initialTime, localId, locals]);

  const handleSave = async () => {
    if (!date) {
      toast.error("Selecione uma data.");
      return;
    }

    const [startHour, startMinute] = startTime.split(":").map(Number);
    const [endHour, endMinute] = endTime.split(":").map(Number);

    const startDate = date.hour(startHour).minute(startMinute).second(0).millisecond(0);
    const endDate = date.hour(endHour).minute(endMinute).second(0).millisecond(0);

    if (!startDate.isBefore(endDate)) {
      toast.error("Horário inicial deve ser menor que o horário final.");
      return;
    }

    // Garantir que temos os dados do médico e do local
    if (!doctorId || !selectedLocalId) {
      toast.error("Selecione o médico e o local para criar o horário.");
      return;
    }

    setLoading(true);
    try {
      await createDoctorSchedule({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        doctorId,
        localId: selectedLocalId,
        status: "ATIVO",
      });

      if (onScheduleCreated) {
        onScheduleCreated();
      }

      toast.success("Horário criado com sucesso!");
      onClose();
    } catch (e: any) {
      toast.error("Não foi possível salvar o horário. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Definir horários disponíveis</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Escolha o local de atendimento, a data e o intervalo de horários em que você estará atendendo.
            </Typography>

            {locals && locals.length > 0 && (
              <FormControl size="small" fullWidth>
                <InputLabel id="schedule-local-select-label">Local de atendimento</InputLabel>
                <Select
                  labelId="schedule-local-select-label"
                  value={selectedLocalId || ""}
                  label="Local de atendimento"
                  onChange={(e) => setSelectedLocalId(e.target.value as string)}
                >
                  {locals.map((local) => (
                    <MenuItem key={local.id} value={local.id}>
                      {local.localName || local.address || local.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <DateCalendar
                disablePast
                value={date}
                onChange={(newValue) => setDate(newValue)}
              />
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Início"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                fullWidth
                inputProps={{ step: 300 }}
              />
              <TextField
                label="Fim"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                fullWidth
                inputProps={{ step: 300 }}
              />
            </Stack>
          </Stack>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={loading}>
          Cancelar
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {loading ? "Salvando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}